import type { EventManager } from '@arkturian/audio-guide';
import type { SongItem } from '@arkturian/audio-guide';
import type { IView } from '../IView';

/**
 * YoutubePanelView — renders:
 *   #nowPlaying    — currently playing song
 *   #songQueue     — upcoming songs (pending in queue)
 *   #songHistory   — previously played songs (newest first)
 *   #ytNextBtn     — skip to next song
 */
export class YoutubePanelView implements IView {
  private _wrap: HTMLElement | null = null;
  private _nowPlaying: HTMLElement | null = null;
  private _songQueue: HTMLElement | null = null;
  private _songHistory: HTMLElement | null = null;
  private _nextBtn: HTMLButtonElement | null = null;
  private _bus: EventManager | null = null;
  private _nextHandler: EventListener | null = null;
  private _history: string[] = [];
  private _lastCurrent: string | null = null;

  private _onSongsChanged = (payload: { current: SongItem | null; queue: SongItem[] }): void => {
    // Track history: when a new song starts playing, push the old one to history
    if (payload.current) {
      const label = payload.current.title || payload.current.query;
      if (this._lastCurrent && this._lastCurrent !== label) {
        this._history.unshift(this._lastCurrent);
        if (this._history.length > 20) this._history.pop();
      }
      this._lastCurrent = label;
    }

    // Show/hide pill + update Now Playing
    if (this._wrap) {
      this._wrap.style.display = payload.current ? 'flex' : 'none';
    }
    if (this._nowPlaying) {
      if (payload.current) {
        const label = payload.current.title || payload.current.query;
        this._nowPlaying.textContent = `♪ ${label}`;
      } else {
        this._nowPlaying.textContent = '♪';
      }
    }

    // Queue (upcoming)
    if (this._songQueue) {
      if (payload.queue.length === 0) {
        this._songQueue.textContent = '';
      } else {
        this._songQueue.innerHTML =
          '<div style="color:#7aa2f7;font-weight:700;margin-bottom:2px">⏭ Als nächstes:</div>' +
          payload.queue
            .map((s, i) => `<div>${i + 1}. ${s.title || s.query}</div>`)
            .join('');
      }
    }

    // History (previously played)
    if (this._songHistory) {
      if (this._history.length === 0) {
        this._songHistory.textContent = '';
      } else {
        this._songHistory.innerHTML =
          '<div style="color:#565f89;font-weight:700;margin-bottom:2px">♪ Gespielt:</div>' +
          this._history
            .map((s) => `<div style="color:#414868">· ${s}</div>`)
            .join('');
      }
    }
  };

  mount(root: HTMLElement): void {
    this._wrap = root.querySelector<HTMLElement>('#ytWrap');
    this._nowPlaying = root.querySelector<HTMLElement>('#nowPlaying');
    this._songQueue = root.querySelector<HTMLElement>('#songQueue');
    this._songHistory = root.querySelector<HTMLElement>('#songHistory');
    this._nextBtn = root.querySelector<HTMLButtonElement>('#ytNextBtn');
  }

  bindEvents(bus: EventManager): void {
    this._bus = bus;
    bus.on('songs:changed', this._onSongsChanged);
    if (this._nextBtn) {
      this._nextHandler = () => bus.emit('user:yt-next');
      this._nextBtn.addEventListener('click', this._nextHandler);
    }
  }

  dispose(): void {
    if (this._bus) {
      this._bus.off('songs:changed', this._onSongsChanged);
      this._bus = null;
    }
    if (this._nextBtn && this._nextHandler) {
      this._nextBtn.removeEventListener('click', this._nextHandler);
      this._nextHandler = null;
    }
  }
}
